import type { MailDraft, OrderProductLine, TemplateKey } from "./types";

export const TEMPLATE_LABEL: Record<TemplateKey, string> = {
  uitverkocht: "Uitverkocht, binnenkort voorraad",
  kleur_niet_mengbaar: "Kleur niet mengbaar",
  niet_meer_leverbaar: "Niet meer leverbaar",
};

export const TEMPLATE_SINGULAR_TEXT: Record<
  TemplateKey,
  {
    subject: string;
    body: string;
  }
> = {
  uitverkocht: {
    subject: "Update over je bestelling, {MERK} {PRODUCT} tijdelijk uitverkocht",
    body:
      "Helaas is de {MERK} {PRODUCT} momenteel uitverkocht, excuses voor het ongemak.\n\n" +
      "Wij verwachten binnenkort weer voorraad te ontvangen. Mocht je het willen, kunnen we eventueel ook een alternatief product zoeken voor je klus. Geef dan graag aan ons door wat je gaat doen met het product, dan zoeken wij een passend alternatief.",
  },
  kleur_niet_mengbaar: {
    subject: "Update over je bestelling, kleur {KLEUR} niet mengbaar in {MERK} {PRODUCT}",
    body:
      "Helaas kan de door jou gekozen kleur {KLEUR} niet worden gemengd in de {MERK} {PRODUCT}.\n\n" +
      "Heb je deze kleur eerder gehad in dit product? Daarnaast zijn we benieuwd of je de kleur in de kleurentool vanaf het scherm hebt gekozen of vanaf een officiele kleurstaal. Eventueel kunnen we dan een kleur erbij zoeken die wel te maken is in het door jou gekozen product.",
  },
  niet_meer_leverbaar: {
    subject: "Update over je bestelling, {MERK} {PRODUCT} niet meer leverbaar",
    body:
      "Vanuit de leverancier hebben wij te horen gekregen dat de {MERK} {PRODUCT} niet meer leverbaar is, excuses voor het ongemak.\n\n" +
      "Mocht je het willen, kunnen we een alternatief product zoeken voor je klus. Geef dan graag aan ons door wat je gaat doen met het product, dan zoeken wij een passend alternatief. Uiteraard heb je ook de mogelijkheid om de bestelling te annuleren.",
  },
};

export const TEMPLATE_PLURAL_TEXT: Record<
  TemplateKey,
  {
    subject: string;
    body: string;
  }
> = {
  uitverkocht: {
    subject: "Update over je bestelling, meerdere producten tijdelijk uitverkocht",
    body:
      "Helaas zijn de volgende producten momenteel uitverkocht, excuses voor het ongemak:\n\n{BULLETS}\n\n" +
      "Wij verwachten binnenkort weer voorraad te ontvangen. Mocht je het willen, kunnen we eventueel ook alternatieve producten zoeken voor je klus. Geef dan graag aan ons door wat je gaat doen met de producten, dan zoeken wij passende alternatieven.",
  },
  kleur_niet_mengbaar: {
    subject: "Update over je bestelling, gekozen kleuren niet mengbaar in meerdere producten",
    body:
      "Helaas kunnen de door jou gekozen kleuren niet worden gemengd in de volgende producten:\n\n{BULLETS}\n\n" +
      "Heb je deze kleuren eerder gehad in deze producten? Daarnaast zijn we benieuwd of je de kleuren in de kleurentool vanaf het scherm hebt gekozen of vanaf een officiele kleurstaal. Eventueel kunnen we dan kleuren erbij zoeken die wel te maken zijn in de door jou gekozen producten.",
  },
  niet_meer_leverbaar: {
    subject: "Update over je bestelling, meerdere producten niet meer leverbaar",
    body:
      "Vanuit de leverancier hebben wij te horen gekregen dat de volgende producten niet meer leverbaar zijn, excuses voor het ongemak:\n\n{BULLETS}\n\n" +
      "Mocht je het willen, kunnen we alternatieve producten zoeken voor je klus. Geef dan graag aan ons door wat je gaat doen met de producten, dan zoeken wij passende alternatieven. Uiteraard heb je ook de mogelijkheid om de bestelling te annuleren.",
  },
};

export const TEMPLATE_OPTIONS = (Object.keys(TEMPLATE_LABEL) as TemplateKey[]).map((key) => ({
  key,
  label: TEMPLATE_LABEL[key],
}));

export const parseKleurFromCustomFields = (customFields: unknown): string => {
  if (!Array.isArray(customFields)) return "";

  for (const field of customFields as Array<Record<string, unknown>>) {
    const title = String(field?.title ?? "").trim().toLowerCase();
    if (title !== "kleurnummer" && title !== "kleur") continue;

    const values = Array.isArray(field?.values) ? field.values : [];
    for (const value of values as Array<Record<string, unknown>>) {
      const rawValue = value?.value;
      if (rawValue === false || rawValue == null) continue;
      const normalized = String(rawValue).trim();
      if (normalized) return normalized.split(" |")[0].trim();
    }
  }

  return "";
};

const normalizeWhitespace = (value: string) => value.replace(/\s+/g, " ").trim();

const SIZE_UNIT_REGEX = /^\d+\s*(ml|cl|l|ltr|liter|cm|mm|m|kg|g|stuks|stuk|st)$/i;

export function extractNameFromBilling(
  billing: Record<string, unknown> | null | undefined,
): string {
  if (!billing || typeof billing !== "object") return "";

  const get = (k: string) => {
    const v = (billing as any)[k];
    return typeof v === "string" ? v.trim() : "";
  };

  const first = get("firstname") || get("first_name") || get("voornaam");
  const last = get("lastname") || get("last_name") || get("achternaam");

  return [first, last].filter(Boolean).join(" ").trim();
}

export function sanitizeProductTitle(raw: string | null | undefined): string {
  if (!raw) return "";
  let title = String(raw);
  title = title.replace(/\|\d+\s*$/, "").trim();
  const dashIdx = title.lastIndexOf(" - ");
  if (dashIdx > 0) {
    const tail = title.slice(dashIdx + 3).trim();
    if (!SIZE_UNIT_REGEX.test(tail)) {
      title = title.slice(0, dashIdx).trim();
    }
  }
  title = title.replace(/\s+/g, " ").trim();
  return title;
}

const replacePlaceholders = (text: string, productLine: OrderProductLine) => {
  const voornaam = (productLine.voornaam_klant ?? "").trim();
  return text
    .replace(/\{MERK\}/g, productLine.brand_title ?? "")
    .replace(/\{PRODUCT\}/g, sanitizeProductTitle(productLine.producttitle))
    .replace(/\{KLEUR\}/g, productLine.color ?? "")
    .replace(/\{VOORNAAM\}/g, voornaam || "klant");
};

export const describeProductLine = (productLine: OrderProductLine): string => {
  const brand = (productLine.brand_title ?? "").trim();
  let product = sanitizeProductTitle(productLine.producttitle);
  let variant = (productLine.varianttitle ?? "").trim();


  if (brand && product.toLowerCase().startsWith(brand.toLowerCase())) {
    product = product.slice(brand.length).trim();
  }

  if (variant && product.toLowerCase().includes(variant.toLowerCase())) {
    variant = "";
  }

  const parts = [brand, product, variant].filter((part) => part.length > 0);
  const joined = normalizeWhitespace(parts.join(" "));

  return joined || productLine.sku || "Onbekend product";
};

export const buildSubjectAndBody = (
  template_key: TemplateKey,
  productLines: OrderProductLine[],
): Pick<MailDraft, "subject" | "body"> => {
  const primaryLine = productLines[0];
  if (!primaryLine) {
    return {
      subject: "Update over je bestelling",
      body: "Er is een update over je bestelling.",
    };
  }

  const emailSuffix = primaryLine.emailadres_klant ? ` [klant: ${primaryLine.emailadres_klant}]` : "";

  if (productLines.length === 1) {
    const template = TEMPLATE_SINGULAR_TEXT[template_key];
    return {
      subject: `${normalizeWhitespace(replacePlaceholders(template.subject, primaryLine))}${emailSuffix}`,
      body: replacePlaceholders(template.body, primaryLine),
    };
  }

  const template = TEMPLATE_PLURAL_TEXT[template_key];
  const bulletList = productLines.map((line) => `• ${describeProductLine(line)}`).join("\n");
  const bodyWithBullets = template.body.replace("{BULLETS}", bulletList);

  return {
    subject: `${normalizeWhitespace(replacePlaceholders(template.subject, primaryLine))}${emailSuffix}`,
    body: replacePlaceholders(bodyWithBullets, primaryLine),
  };

};
